import * as React from 'react';
import {Rive as RiveClass} from '@rive-app/webgl2';

const {useRef, useEffect, useState, memo} = React;

const PLAYBACK_DELAY = 200;

const useHoverPlay = (rive: RiveClass | null, hover: boolean) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  useEffect(() => {
    if (!rive) return;

    clearTimeout(timeoutRef.current);

    if (hover) {
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      timeoutRef.current = setTimeout(() => {
        rive.play();
      }, PLAYBACK_DELAY);
    } else {
      rive.pause();
      rive.reset();
    }

    return () => clearTimeout(timeoutRef.current);
  }, [hover, rive]);
};

export const Rive: React.ComponentType<{
  src: string;
  hover: boolean;
  style?: React.CSSProperties;
  ['data-location']?: string;
  onError?: () => void;
}> = memo(function Rive({
  src,
  hover,
  style,
  'data-location': dataLocation,
  onError,
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [riveInstance, setRiveInstance] = useState<RiveClass | null>(null);
  const onErrorRef = useRef(onError);
  // eslint-disable-next-line react-hooks/react-compiler
  onErrorRef.current = onError;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;
    const rive = new RiveClass({
      canvas,
      src,
      autoplay: false,
      onLoad: () => {
        if (cancelled) return;
        rive.resizeDrawingSurfaceToCanvas();
        rive.pause();
        setRiveInstance(rive);
      },
      onLoadError: () => {
        if (!cancelled) onErrorRef.current?.();
      },
    });

    return () => {
      cancelled = true;
      rive.cleanup();
      setRiveInstance(null);
    };
  }, [src]);

  useHoverPlay(riveInstance, hover);

  const additionalProps: Record<string, string> = {};
  if (dataLocation) {
    additionalProps['data-location'] = dataLocation;
  }

  return (
    <canvas
      ref={canvasRef}
      data-automation-id="rive-player"
      {...additionalProps}
      style={style}
    />
  );
});
